import React, { useEffect, useState } from 'react';
import { Card, Button, Form, Input, message, Skeleton } from 'antd';
import {
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  SendOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import api from '../services/api';
import './Home.css';
import './Contact.css';

const { TextArea } = Input;

const WHATSAPP_URL = 'https://wa.me/9779805446865';

// Default (fallback) contact details, mirror the site footer (Nepal).
const DEFAULT_CONTACT_INFO = [
  {
    key: 'address',
    title: 'Visit us',
    content: 'New Road, Kathmandu, Nepal',
    subtext: 'Showroom open Sun–Fri, 10 AM – 6 PM',
  },
  {
    key: 'phone',
    title: 'Call us',
    content: '+977 980-544-6865',
    subtext: 'Sun–Fri: 10 AM – 6 PM',
  },
  {
    key: 'email',
    title: 'Email us',
    content: 'info@currentcarenpl.com',
    subtext: 'We reply within 24 hours',
  },
  {
    key: 'hours',
    title: 'Support hours',
    content: 'Sunday – Friday',
    subtext: '10:00 AM – 6:00 PM (Saturday closed)',
  },
];

// Pick a boxed icon + accent colour for each info card.
const iconFor = (item) => {
  const label = `${item.key || ''} ${item.title || ''}`.toLowerCase();
  if (label.includes('address') || label.includes('visit') || label.includes('location')) {
    return { icon: <EnvironmentOutlined />, cls: '' };
  }
  if (label.includes('email') || label.includes('mail')) {
    return { icon: <MailOutlined />, cls: 'amber' };
  }
  if (label.includes('hour') || label.includes('emergency') || label.includes('time')) {
    return { icon: <ClockCircleOutlined />, cls: 'green' };
  }
  return { icon: <PhoneOutlined />, cls: '' };
};

// Turn a content value into a clickable link where it makes sense.
const linkFor = (item) => {
  const label = `${item.key || ''} ${item.title || ''}`.toLowerCase();
  const val = String(item.content || '');
  if (label.includes('phone') || label.includes('call')) {
    return `tel:${val.replace(/[^+\d]/g, '')}`;
  }
  if (label.includes('email') || label.includes('mail')) {
    return `mailto:${val}`;
  }
  return null;
};

const Contact = () => {
  const [form] = Form.useForm();
  const [formLoading, setFormLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const contactInfo = DEFAULT_CONTACT_INFO;

  const onFinish = async (values) => {
    setFormLoading(true);
    try {
      await api.post('/contact', values);
      message.success('Your message has been sent successfully! We will get back to you soon.');
      form.resetFields();
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to send message. Please try again.';
      message.error(msg);
    } finally {
      setFormLoading(false);
    }
  };

  useEffect(() => {
    setPageLoading(false);
  }, []);

  if (pageLoading) {
    return (
      <div style={{ padding: '40px' }}>
        <Skeleton active paragraph={{ rows: 12 }} />
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* Two-column: info + form */}
      <section className="home-section" style={{ paddingTop: 56 }}>
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Left, contact info + map */}
          <div>
            <h2 className="mb-1 font-display text-2xl font-extrabold text-slate-900">
              Reach us directly
            </h2>
            <p className="mb-6 text-slate-500">
              Prefer to talk? Use any of the channels below or drop by our New Road showroom.
            </p>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {contactInfo.map((info) => {
                const { icon, cls } = iconFor(info);
                const href = linkFor(info);
                return (
                  <div className="contact-info-card" key={info.key || info.title}>
                    <span className={`contact-info-icon ${cls}`}>{icon}</span>
                    <div className="min-w-0">
                      <div className="contact-info-title">{info.title}</div>
                      <div className="contact-info-value">
                        {href ? <a href={href}>{info.content}</a> : info.content}
                      </div>
                      <div className="contact-info-sub">{info.subtext}</div>
                    </div>
                  </div>
                );
              })}

              {/* WhatsApp card */}
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-info-card sm:col-span-2"
              >
                <span className="contact-info-icon green">
                  <WhatsAppOutlined />
                </span>
                <div className="min-w-0">
                  <div className="contact-info-title">WhatsApp</div>
                  <div className="contact-info-value">Chat with us on WhatsApp</div>
                  <div className="contact-info-sub">Fastest way to reach our support team</div>
                </div>
              </a>
            </div>

            {/* Map */}
            <div className="contact-map mt-6">
              <iframe
                title="Current Care location, New Road, Kathmandu"
                src="https://maps.google.com/maps?q=New%20Road%2C%20Kathmandu%2C%20Nepal&t=&z=15&ie=UTF8&iwloc=&output=embed"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Right, form */}
          <div id="contact-form">
            <Card className="contact-form-card">
              <h2 className="mb-1 font-display text-2xl font-extrabold text-slate-900">
                Send us a message
              </h2>
              <p className="mb-6 text-slate-500">
                Fill out the form and we&apos;ll get back to you as soon as possible.
              </p>
              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                size="large"
                requiredMark={false}
              >
                <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
                  <Form.Item
                    name="name"
                    label="Full Name"
                    rules={[{ required: true, message: 'Please enter your full name' }]}
                  >
                    <Input placeholder="Ram Bahadur" />
                  </Form.Item>

                  <Form.Item
                    name="phone"
                    label="Phone Number"
                    rules={[{ pattern: /^\+?\d+$/, message: 'Please enter numbers and + only' }]}
                  >
                    <Input
                      type="tel"
                      onKeyPress={(e) =>
                        e.key !== '+' && !/[0-9]/.test(e.key) && e.preventDefault()
                      }
                      placeholder="+977 98XXXXXXXX"
                    />
                  </Form.Item>
                </div>

                <Form.Item
                  name="email"
                  label="Email Address"
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Please enter a valid email' },
                  ]}
                >
                  <Input placeholder="you@example.com" />
                </Form.Item>

                <Form.Item
                  name="subject"
                  label="Subject"
                  rules={[{ required: true, message: 'Please enter a subject' }]}
                >
                  <Input placeholder="How can we help you?" />
                </Form.Item>

                <Form.Item
                  name="department"
                  label="Department"
                  rules={[{ required: true, message: 'Please select a department' }]}
                >
                  <Input placeholder="Sales, Repair & Installation, Order support…" />
                </Form.Item>

                <Form.Item
                  name="message"
                  label="Message"
                  rules={[{ required: true, message: 'Please enter your message' }]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Tell us more about how we can help you..."
                    maxLength={500}
                    showCount
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={formLoading}
                    icon={<SendOutlined />}
                    size="large"
                    block
                  >
                    Send Message
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </div>
        </div>
      </section>

      {/* Assistance CTA band */}
      <section className="home-section" style={{ paddingTop: 0 }}>
        <div className="contact-cta-band">
          <h3>Need help right away?</h3>
          <p>
            Our team is ready to help with product advice, repair bookings, and order support. Call
            us or start a WhatsApp chat.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button type="primary" size="large" icon={<PhoneOutlined />} href="tel:+9779805446865">
              +977 980-544-6865
            </Button>
            <Button
              size="large"
              icon={<WhatsAppOutlined />}
              href={WHATSAPP_URL}
              target="_blank"
              style={{
                background: 'rgba(255,255,255,0.08)',
                borderColor: 'rgba(255,255,255,0.35)',
                color: '#ffffff',
              }}
            >
              Chat on WhatsApp
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
